import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw, Home, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

/**
 * Per-route Error Boundary.
 *
 * Wraps a group of routes so a render-time error in one module (e.g.
 * Production) does not unmount the whole platform shell. The fallback UI
 * exposes a friendly message, a retry action that resets the boundary,
 * a shortcut back to the dashboard, and a stable error code that the
 * user can copy for the support team.
 *
 * This component intentionally lives outside the React Router data-router
 * surface so it also works with the legacy `<Routes>` setup used by the
 * app. It composes naturally with `errorElement` for data routers (the
 * router-level errorElement still wins for loader/action errors).
 */
interface Props {
  /** Human-readable module name shown in the fallback, e.g. "Producción". */
  scope?: string;
  /** Override the default "back to dashboard" CTA target. */
  homeHref?: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
  errorId: string | null;
  copied: boolean;
}

/** Short, stable-ish identifier (timestamp + 6 hex) for support traceability. */
function makeErrorId() {
  const ts = Date.now().toString(36).toUpperCase();
  const rnd = Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0').toUpperCase();
  return `ERR-${ts}-${rnd}`;
}

export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null, errorId: null, copied: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, errorId: makeErrorId(), copied: false };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to the console for the in-app Error Monitor / external reporters.
    // Avoid throwing in the handler itself.
    // eslint-disable-next-line no-console
    console.error(
      `[RouteErrorBoundary${this.props.scope ? `:${this.props.scope}` : ''}]`,
      error,
      info.componentStack,
    );
  }

  private handleRetry = () => {
    this.setState({ error: null, errorId: null, copied: false });
  };

  private handleCopy = async () => {
    const { error, errorId } = this.state;
    if (!error || !errorId) return;
    const payload = `${errorId}\n${this.props.scope ?? 'unknown'}\n${error.name}: ${error.message}`;
    try {
      await navigator.clipboard.writeText(payload);
      this.setState({ copied: true });
      window.setTimeout(() => this.setState({ copied: false }), 1500);
    } catch {
      /* clipboard unavailable — ignore silently */
    }
  };

  render() {
    const { error, errorId, copied } = this.state;
    if (!error) return this.props.children;

    const { scope, homeHref = '/dashboard' } = this.props;

    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Card className="w-full max-w-lg border-destructive/30">
          <CardHeader className="space-y-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <CardTitle className="text-lg">Algo salió mal en esta sección</CardTitle>
            <CardDescription>
              {scope
                ? `Se produjo un error al cargar el módulo de ${scope}.`
                : 'Se produjo un error inesperado al cargar esta vista.'}{' '}
              Puedes reintentar la operación o volver al panel principal.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-md border bg-muted/40 p-3 text-xs">
              <div className="mb-1 font-medium text-foreground">Detalle técnico</div>
              <div className="font-mono text-[11px] text-muted-foreground break-words">
                {error.name}: {error.message}
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 rounded-md border border-dashed bg-card px-3 py-2">
              <div className="text-[11px] text-muted-foreground">
                Código de error
                <div className="font-mono text-xs text-foreground">{errorId}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={this.handleCopy} className="h-8 text-xs">
                {copied ? (
                  <><Check className="h-3.5 w-3.5 mr-1" /> Copiado</>
                ) : (
                  <><Copy className="h-3.5 w-3.5 mr-1" /> Copiar</>
                )}
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" onClick={this.handleRetry}>
                <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reintentar
              </Button>
              <Button asChild variant="outline" size="sm">
                <a href={homeHref}>
                  <Home className="h-3.5 w-3.5 mr-1" /> Volver al Dashboard
                </a>
              </Button>
              <span className="ml-auto text-[11px] text-muted-foreground">
                Si el problema persiste, comparte el código con soporte.
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}

/**
 * Convenience element factory for React Router data-routers:
 *
 * ```tsx
 * { path: 'production', element: <DispatchBoard/>,
 *   errorElement: routeErrorElement('Producción') }
 * ```
 */
export function routeErrorElement(scope?: string) {
  return <RouteErrorBoundary scope={scope}><div /></RouteErrorBoundary>;
}

export default RouteErrorBoundary;
